import {
	EMPTY_CRITERIA,
	PeopleSearchCriteria,
	PeopleSearchDefinitionV1,
	cloneCriteria,
} from "@/types/peopleSearch";

// Serialize criteria to a URL-safe base64 string (same approach as serializeQueryToURL in queryBuilder.ts)
export function serializeCriteriaToURL(criteria: PeopleSearchCriteria): string {
	try {
		const definition: PeopleSearchDefinitionV1 = { kind: 'people-search-v1', criteria };
		// Unicode-safe base64 (chip values may contain accents)
		return btoa(encodeURIComponent(JSON.stringify(definition)));
	} catch {
		return '';
	}
}

export function deserializeCriteriaFromURL(encoded: string): PeopleSearchCriteria | null {
	try {
		const parsed = JSON.parse(decodeURIComponent(atob(encoded)));
		if (!parsed || typeof parsed !== 'object' || parsed.kind !== 'people-search-v1')
			return null;
		return normalizeCriteria(parsed.criteria);
	} catch {
		return null;
	}
}

// Merge a possibly-partial/older payload onto the empty shape so every category slice exists
function normalizeCriteria(raw: unknown): PeopleSearchCriteria | null {
	if (!raw || typeof raw !== 'object')
		return null;
	const base = cloneCriteria(EMPTY_CRITERIA);
	const input = raw as Partial<PeopleSearchCriteria>;

	if (input.role)
		base.role = { titles: asStringArray(input.role.titles), timeframe: asTimeframe(input.role.timeframe) };
	if (input.company)
		base.company = { names: asStringArray(input.company.names), timeframe: asTimeframe(input.company.timeframe) };
	if (input.location)
		base.location = { values: asStringArray(input.location.values) };
	if (input.keywords)
		base.keywords = {
			required: asStringArray(input.keywords.required),
			anyOfGroups: Array.isArray(input.keywords.anyOfGroups)
				? input.keywords.anyOfGroups.map(asStringArray).filter(g => g.length > 0)
				: [],
			excluded: asStringArray(input.keywords.excluded),
		};
	if (input.skills)
		base.skills = { names: asStringArray(input.skills.names), mode: input.skills.mode === 'all' ? 'all' : 'any' };
	if (input.languages && Array.isArray(input.languages.items))
		base.languages = {
			items: input.languages.items
				.filter(item => item && typeof item.code === 'string')
				.map(item => ({
					code: item.code,
					label: typeof item.label === 'string' ? item.label : item.code,
					minCefr: typeof item.minCefr === 'number' ? item.minCefr : null,
				})),
		};
	if (input.status)
		base.status = { values: asStringArray(input.status.values) };
	if (input.lists)
		base.lists = { ids: asStringArray(input.lists.ids) };
	if (input.profile)
		base.profile = {
			name: typeof input.profile.name === 'string' ? input.profile.name : '',
			managerIds: asStringArray(input.profile.managerIds),
			createdFrom: typeof input.profile.createdFrom === 'string' ? input.profile.createdFrom : null,
			createdTo: typeof input.profile.createdTo === 'string' ? input.profile.createdTo : null,
		};

	return base;
}

function asStringArray(value: unknown): string[] {
	return Array.isArray(value) ? value.filter((v): v is string => typeof v === 'string') : [];
}

function asTimeframe(value: unknown): 'current' | 'former' | 'both' {
	return value === 'current' || value === 'former' ? value : 'both';
}
